import axios from 'axios';
import { getIdToken, getCurrentSession, signOut } from './auth.service';

// Create axios instance
// In development, use relative path so Vite proxy handles CORS
// In production, use full URL from env var or default to /api/account
const baseURL = import.meta.env.DEV
  ? '/api/account'
  : (import.meta.env.VITE_ACCOUNT_API_BASE_URL || '/api/account');

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - Add JWT token to every request
apiClient.interceptors.request.use(
  async (config) => {
    let token = getIdToken();

    // If no token, try to get current session (will auto-refresh if needed)
    if (!token) {
      try {
        const session = await getCurrentSession();
        token = session.idToken;
      } catch (error) {
        // No valid session, request will proceed without token
        console.warn('No valid auth session');
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors (token expired/invalid)
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh session
        const session = await getCurrentSession();
        const newToken = session.idToken;

        // Update authorization header
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - sign out user
        await signOut();

        // Redirect to login (you can customize this)
        window.location.href = '/login';

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// ============================================
// API METHODS (Examples)
// ============================================

export const accountApi = {
  // Get user profile
  getProfile: () => apiClient.get('/profile'),

  // Update user profile
  updateProfile: (data) => apiClient.patch('/profile', data),

  // Get search history
  getSearchHistory: (limit = 20) => apiClient.get(`/searches?limit=${limit}`),

  // Delete search history item
  deleteSearch: (searchId) => apiClient.delete(`/searches/${searchId}`),

  // Get liked places
  getLikedPlaces: (limit = 20) => apiClient.get(`/likes/places?limit=${limit}`),

  // Like a place
  likePlace: (placeId) => apiClient.post(`/likes/places/${placeId}`),

  // Unlike a place
  unlikePlace: (placeId) => apiClient.delete(`/likes/places/${placeId}`),

  // Get liked events
  getLikedEvents: (limit = 20) => apiClient.get(`/likes/events?limit=${limit}`),

  // Like an event
  likeEvent: (eventId) => apiClient.post(`/likes/events/${eventId}`),

  // Unlike an event
  unlikeEvent: (eventId) => apiClient.delete(`/likes/events/${eventId}`),

  // Check like status
  getLikeStatus: (placeIds = [], eventIds = []) => {
    const params = new URLSearchParams();
    placeIds.forEach(id => params.append('placeId', id));
    eventIds.forEach(id => params.append('eventId', id));
    return apiClient.get(`/likes/status?${params.toString()}`);
  },
};
